import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import type { BookedHoursRow } from "@/lib/bookedHours";
import { formatBookedHoursWhen, formatHours } from "@/lib/bookedHours";
import { updateEmployeeTime } from "@/lib/employeeTimes";
import { toast } from "@/lib/toast";
import { AngebotPositionSelect } from "@/pages/admin/zeiterfassung/AngebotPositionSelect";
import { BookedHoursRebookDialog } from "@/projectOverview/BookedHoursRebookDialog";
import { PROJECT_OVERVIEW_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

const checkboxClass =
  "h-4 w-4 shrink-0 rounded border-separator-opaque text-accent focus:ring-accent-soft";

export function BookedHoursDetailModal({
  open,
  onClose,
  auftragsnummer,
  projectId,
  canRebook = false,
  onRebooked,
  onUpdated,
  rows,
  totalHours,
  loading,
  error,
  heroError,
  subcontractorError,
  employeeError = false,
}: {
  open: boolean;
  onClose: () => void;
  auftragsnummer?: string | null;
  projectId?: number;
  canRebook?: boolean;
  onRebooked?: () => void;
  onUpdated?: () => void;
  rows: BookedHoursRow[];
  totalHours: number;
  loading: boolean;
  error: boolean;
  heroError: boolean;
  subcontractorError: boolean;
  employeeError?: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [rebookOpen, setRebookOpen] = useState(false);
  const [positionEdit, setPositionEdit] = useState<BookedHoursRow | null>(null);
  const selectAllRefs = useRef<Array<HTMLInputElement | null>>([]);
  const allowRebook = canRebook && projectId != null && projectId > 0;

  const title = auftragsnummer
    ? `Gebuchte Stunden · ${auftragsnummer}`
    : "Gebuchte Stunden";

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setRebookOpen(false);
      setPositionEdit(null);
    }
  }, [open]);

  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(rows.map((row) => row.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  useEffect(() => {
    if (!positionEdit) return;
    if (!rows.some((row) => row.id === positionEdit.id)) setPositionEdit(null);
  }, [rows, positionEdit]);

  const partialWarning = useMemo(() => {
    const parts: string[] = [];
    if (heroError) parts.push("Hero-Zeiten");
    if (subcontractorError) parts.push("Nachunternehmer-Zeiten");
    if (employeeError) parts.push("App-Zeiten");
    if (!parts.length) return null;
    if (parts.length === 1) {
      return `${parts[0]} konnten nicht vollständig geladen werden.`;
    }
    if (parts.length === 2) {
      return `${parts[0]} und ${parts[1]} konnten nicht vollständig geladen werden.`;
    }
    return `${parts.slice(0, -1).join(", ")} und ${parts[parts.length - 1]} konnten nicht vollständig geladen werden.`;
  }, [heroError, subcontractorError, employeeError]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selected.has(row.id)),
    [rows, selected],
  );
  const selectedHours = selectedRows.reduce((sum, row) => sum + row.hours, 0);
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  useEffect(() => {
    const indeterminate = selectedRows.length > 0 && !allSelected;
    for (const node of selectAllRefs.current) {
      if (node) node.indeterminate = indeterminate;
    }
  }, [allSelected, selectedRows.length]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(rows.map((row) => row.id)));
  }

  const labelSpan = allowRebook ? 3 : 2;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={title}
        description={
          allowRebook
            ? "Einzelbuchungen nach Person, Datum, Position und Quelle. Die Position von App-Zeiten kann hier korrigiert werden. Markierte Stunden können auf ein anderes Projekt umgebucht werden."
            : "Einzelbuchungen nach Person, Datum, Position und Quelle."
        }
        size="3xl"
        fullScreenMobile
        zIndexClass={PROJECT_OVERVIEW_OVERLAY_Z}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              Schließen
            </Button>
            {allowRebook && selectedRows.length > 0 ? (
              <Button type="button" onClick={() => setRebookOpen(true)}>
                {formatHours(selectedHours)} umbuchen
              </Button>
            ) : null}
          </>
        }
      >
        {loading ? (
          <p className="text-sm text-label-tertiary">Lade gebuchte Stunden …</p>
        ) : error ? (
          <p className="text-sm text-status-red">
            Gebuchte Stunden konnten nicht geladen werden.
          </p>
        ) : !rows.length ? (
          <p className="text-sm text-label-tertiary">Noch keine Stunden auf diesen Auftrag gebucht.</p>
        ) : (
          <>
            {partialWarning ? (
              <p className="mb-3 text-sm text-status-orange">{partialWarning}</p>
            ) : null}
            {allowRebook ? (
              <label className="mb-3 flex cursor-pointer items-center gap-2 text-sm text-label md:hidden">
                <input
                  ref={(node) => {
                    selectAllRefs.current[0] = node;
                  }}
                  type="checkbox"
                  className={checkboxClass}
                  checked={allSelected}
                  onChange={toggleAll}
                />
                Alle markieren
              </label>
            ) : null}
            <ul className="space-y-2 md:hidden" role="list">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className={clsx(
                    "rounded-ios border border-separator bg-fill-quaternary/20 px-3 py-2.5",
                    allowRebook && selected.has(row.id) && "border-accent/40 bg-accent/5",
                  )}
                >
                  <div className="flex gap-2">
                    {allowRebook ? (
                      <input
                        type="checkbox"
                        className={clsx(checkboxClass, "mt-1")}
                        checked={selected.has(row.id)}
                        aria-label={`${row.person}, ${formatHours(row.hours)} auswählen`}
                        onChange={() => toggle(row.id)}
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-label">{row.person}</p>
                          <p className="mt-0.5 tabular-nums text-xs text-label-secondary">
                            {formatBookedHoursWhen(row.when)}
                          </p>
                        </div>
                        <span className="shrink-0 tabular-nums text-sm font-semibold text-label">
                          {formatHours(row.hours)}
                        </span>
                      </div>
                      <div className="mt-2">
                        <PositionCell
                          row={row}
                          canEdit={allowRebook}
                          onEdit={setPositionEdit}
                        />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <SourceBadge row={row} />
                        {row.comment?.trim() ? (
                          <p className="min-w-0 flex-1 text-xs text-label-secondary">
                            {row.comment.trim()}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
              <li className="flex items-center justify-between rounded-ios border border-separator bg-fill-tertiary/40 px-3 py-2.5">
                <span className="text-sm font-semibold text-label">Gesamt</span>
                <span className="tabular-nums text-sm font-semibold text-label">
                  {formatHours(totalHours)}
                </span>
              </li>
            </ul>
            <div className="hidden overflow-x-auto rounded-ios-md border border-separator md:block">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-separator bg-fill-tertiary/60 text-left text-xs uppercase tracking-wide text-label-tertiary">
                    {allowRebook ? (
                      <th className="w-10 px-3 py-2 font-medium">
                        <input
                          ref={(node) => {
                            selectAllRefs.current[1] = node;
                          }}
                          type="checkbox"
                          className={checkboxClass}
                          checked={allSelected}
                          aria-label="Alle Buchungen auswählen"
                          onChange={toggleAll}
                        />
                      </th>
                    ) : null}
                    <th className="px-3 py-2 font-medium">Person</th>
                    <th className="px-3 py-2 font-medium">Datum / Zeit</th>
                    <th className="px-3 py-2 font-medium text-right">Stunden</th>
                    <th className="px-3 py-2 font-medium">Position</th>
                    <th className="px-3 py-2 font-medium">Quelle</th>
                    <th className="px-3 py-2 font-medium">Kommentar</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className={clsx(
                        "border-b border-separator last:border-b-0",
                        allowRebook && selected.has(row.id) && "bg-accent/5",
                      )}
                    >
                      {allowRebook ? (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            className={checkboxClass}
                            checked={selected.has(row.id)}
                            aria-label={`${row.person}, ${formatHours(row.hours)} auswählen`}
                            onChange={() => toggle(row.id)}
                          />
                        </td>
                      ) : null}
                      <td className="px-3 py-2 text-label">{row.person}</td>
                      <td className="px-3 py-2 tabular-nums text-label-secondary">
                        {formatBookedHoursWhen(row.when)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-label">
                        {formatHours(row.hours)}
                      </td>
                      <td className="max-w-[16rem] px-3 py-2">
                        <PositionCell
                          row={row}
                          canEdit={allowRebook}
                          onEdit={setPositionEdit}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <SourceBadge row={row} />
                      </td>
                      <td className="max-w-[12rem] truncate px-3 py-2 text-label-secondary">
                        {row.comment?.trim() || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-fill-tertiary/40">
                    <td className="px-3 py-2 font-semibold text-label" colSpan={labelSpan}>
                      Gesamt
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-label">
                      {formatHours(totalHours)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </Dialog>
      {allowRebook && projectId ? (
        <BookedHoursRebookDialog
          open={rebookOpen}
          projectId={projectId}
          rows={selectedRows}
          onClose={() => setRebookOpen(false)}
          onRebooked={() => {
            setSelected(new Set());
            onRebooked?.();
          }}
        />
      ) : null}
      {allowRebook && projectId && positionEdit?.employeeTimeId ? (
        <AngebotPositionSelect
          key={positionEdit.id}
          auftragId={projectId}
          value={positionEdit.positionId ?? null}
          pickerOpen
          onPickerClose={() => setPositionEdit(null)}
          dialogTitle="Position korrigieren"
          dialogNote={positionDialogNote(positionEdit)}
          label=""
          onChange={(positionId) => saveBookedHoursPosition(positionEdit, positionId, onUpdated)}
        />
      ) : null}
    </>
  );
}

function positionDialogNote(row: BookedHoursRow): string {
  const lines = [
    `${row.person} · ${formatBookedHoursWhen(row.when)} · ${formatHours(row.hours)}`,
    row.positionLabel ? `Aktuell: ${row.positionLabel}` : "Aktuell keine Position",
  ];
  if (row.timesheetLabel) {
    lines.push(
      `Diese Stunden stehen auf ${row.timesheetLabel}. Geändert wird die Buchung, der Stundennachweis bleibt unverändert.`,
    );
  }
  return lines.join("\n");
}

async function saveBookedHoursPosition(
  row: BookedHoursRow,
  positionId: number | null,
  onUpdated?: () => void,
): Promise<void> {
  if (!row.employeeTimeId) return;
  if ((row.positionId ?? null) === positionId) return;
  try {
    await updateEmployeeTime(row.employeeTimeId, {
      geschaeftsdokument_position_id: positionId,
    });
  } catch (err) {
    toast.error(err instanceof Error ? err.message : "Position konnte nicht gespeichert werden.");
    throw err;
  }
  toast.success(positionId == null ? "Position wurde entfernt." : "Position wurde gespeichert.");
  onUpdated?.();
}

function preisartBadgeLabel(value: BookedHoursRow["positionPreisart"]): "Festpreis" | "Nachweis" | null {
  if (value === "nachweis") return "Nachweis";
  if (value === "festpreis") return "Festpreis";
  return null;
}

function PositionCell({
  row,
  canEdit,
  onEdit,
}: {
  row: BookedHoursRow;
  canEdit: boolean;
  onEdit: (row: BookedHoursRow) => void;
}) {
  if (row.source !== "employee") {
    return <span className="text-label-tertiary">—</span>;
  }
  const art = preisartBadgeLabel(row.positionPreisart);
  const editable = canEdit && row.employeeTimeId != null && !row.positionEditBlock;
  const label = row.positionLabel;
  const content = (
    <span className="inline-flex max-w-[16rem] flex-wrap items-center gap-1.5">
      {art ? (
        <span
          className={clsx(
            "rounded-full px-2 py-0.5 text-[11px] font-semibold",
            art === "Nachweis"
              ? "bg-status-orange/15 text-status-orange"
              : "bg-status-blue/15 text-status-blue",
          )}
        >
          {art}
        </span>
      ) : null}
      <span
        className={clsx(
          "min-w-0 truncate",
          !label && (editable ? "font-medium text-accent" : "text-label-tertiary"),
        )}
      >
        {label || (editable ? "Position wählen" : "—")}
      </span>
      {editable && label ? (
        <span className="shrink-0 text-xs font-medium text-accent">korrigieren</span>
      ) : null}
      {canEdit && row.positionEditBlock ? (
        <span className="text-xs text-label-tertiary" title={row.positionEditBlock}>
          nicht änderbar
        </span>
      ) : null}
    </span>
  );
  if (!editable) {
    return <span title={row.positionEditBlock || undefined}>{content}</span>;
  }
  return (
    <button
      type="button"
      className="rounded-md text-left text-label hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      title={label ? "Position korrigieren" : "Position wählen"}
      aria-label={
        label
          ? `Position von ${row.person} korrigieren, aktuell ${label}`
          : `Position für ${row.person} wählen`
      }
      onClick={() => onEdit(row)}
    >
      {content}
    </button>
  );
}

function SourceBadge({ row }: { row: BookedHoursRow }) {
  return (
    <span
      className={clsx(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
        row.source === "hero"
          ? "bg-accent/10 text-accent"
          : "bg-fill-tertiary text-label-secondary",
      )}
    >
      {row.sourceLabel}
    </span>
  );
}
